import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import UmlautHelper from './UmlautHelper';
import UmlautButtons from './UmlautButtons';
import QuestionAudioPlayer from '../shared/QuestionAudioPlayer';
import './WritingPracticeComp.css';

// Get base URL for audio
const getBaseURL = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:3000';
};

const EssayWriting = ({ question, onAnswer, onNext, sessionData = null, langCode = null }) => {
  const [essay, setEssay] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [activeAudioUrl, setActiveAudioUrl] = useState(null);
  const [autoPlayRequested, setAutoPlayRequested] = useState(false);
  const activeAnnotationRef = useRef(null);
  const textareaRef = useRef(null);
  const readingTextRef = useRef(null);
  const promptRef = useRef(null);

  // Reset essay when question changes
  useEffect(() => {
    setEssay('');
    setWordCount(0);
    setActiveAudioUrl(null);
    setAutoPlayRequested(false);
  }, [question['soal-id'] || question.id]);

  // Update word count when essay changes
  useEffect(() => {
    const words = essay.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
  }, [essay]);

  // Get readingText from multiple sources and keep the largest one
  const questionPassage = question.passage || '';
  const questionReadingText = question['reading-text'] || '';
  const questionContext = question.context || '';
  const sessionDataPassage = sessionData?.passage || '';
  let storagePassage = '';
  
  // Try to get passage from localStorage
  try {
    const qpSessionRaw = localStorage.getItem("qp-session");
    if (qpSessionRaw) {
      const parsed = JSON.parse(qpSessionRaw);
      storagePassage = parsed.passage || '';
    } else {
      const qpPracticeRaw = localStorage.getItem("qp-practice");
      if (qpPracticeRaw) {
        const parsed = JSON.parse(qpPracticeRaw);
        storagePassage = parsed.passage || '';
      }
    }
  } catch (error) {
    // Ignore localStorage errors
  }
  
  // Keep the largest readingText from all sources (including sessionData)
  const allSources = [questionPassage, questionReadingText, questionContext, sessionDataPassage, storagePassage];
  const readingText = allSources.reduce((largest, current) => 
    (current && current.length > (largest || '').length) ? current : largest, '');
  
  const prompt = question.text || question['text-soal'] || question.prompt || '';
  const questionText = question.question || '';

  // Build annotations map from question.annotations array
  const annotationsMap = useMemo(() => {
    const map = {};
    
    // First, try to get annotations from question.annotations array (like NotesView)
    if (question.annotations && Array.isArray(question.annotations)) {
      question.annotations.forEach((annotation) => {
        const annId = annotation['ann-id'] || annotation.annId || annotation.id;
        const streamUrl = annotation['stream-url'] || annotation.streamUrl || annotation.stream_url;
        const annType = annotation['ann-type'] || annotation.annType || annotation.type;
        
        // Accept audio annotations with streamUrl
        if (annId && streamUrl && annType === 'audio') {
          map[annId] = {
            ...annotation,
            annId,
            streamUrl,
            annType,
          };
        }
      });
    }
    
    // Also parse HTML for data-ann-id attributes (fallback)
    // This handles cases where annotations are only in HTML but not in question.annotations array
    const htmlToParse = readingText || prompt || '';
    if (htmlToParse && typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
      try {
        const parser = new window.DOMParser();
        const doc = parser.parseFromString(htmlToParse, 'text/html');
        const nodes = doc.querySelectorAll('[data-ann-id]');

        nodes.forEach((node) => {
          const annId = node.getAttribute('data-ann-id');
          const annPath = node.getAttribute('data-ann-path');
          const annType = node.getAttribute('data-ann-type');

          // Only add if not already in map (from annotations array)
          if (annId && annType === 'audio' && !map[annId]) {
            // Try to get streamUrl from question.annotations first
            let streamUrl = null;
            if (question.annotations && Array.isArray(question.annotations)) {
              const matchingAnnotation = question.annotations.find(
                (ann) => (ann['ann-id'] || ann.annId || ann.id) === annId
              );
              if (matchingAnnotation) {
                streamUrl = matchingAnnotation['stream-url'] || matchingAnnotation.streamUrl || matchingAnnotation.stream_url;
              }
            }
            
            // Fallback: convert data-ann-path to streamUrl if no streamUrl found
            if (!streamUrl && annPath) {
              const baseURL = getBaseURL();
              streamUrl = `${baseURL}/${annPath}`;
            }
            
            if (streamUrl) {
              map[annId] = {
                annId,
                streamUrl,
                annType,
              };
            }
          }
        });
      } catch (error) {
        console.warn('EssayWriting: failed to parse annotations from HTML', error);
      }
    }

    console.log('🎵 EssayWriting - Annotations map built:', {
      mapSize: Object.keys(map).length,
      mapKeys: Object.keys(map),
      annotations: map
    });

    return map;
  }, [question.annotations, readingText, prompt]);

  const questionLevelAudioUrl = question?.['audio-url'] || question?.audio_url || null;

  const firstAnnotationAudioUrl = useMemo(() => {
    const tryParse = (html) => {
      if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return null;
      try {
        const doc = new window.DOMParser().parseFromString(html, 'text/html');
        const nodes = doc.querySelectorAll('[data-ann-id]');
        for (const node of nodes) {
          const annId = node.getAttribute('data-ann-id');
          if (annId && annotationsMap[annId]?.streamUrl) {
            return annotationsMap[annId].streamUrl;
          }
        }
      } catch {}
      return null;
    };
    return tryParse(readingText) || tryParse(prompt);
  }, [readingText, prompt, annotationsMap]);

  const displayAudioUrl = questionLevelAudioUrl || firstAnnotationAudioUrl || activeAudioUrl;

  // Enhance HTML with annotation classes for reading text; hide annotation shown via waveform player
  const enhancedReadingText = useMemo(() => {
    if (!readingText || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      return readingText;
    }

    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(readingText, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');

      nodes.forEach((node) => {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]) {
          const isShownAsWaveform = !questionLevelAudioUrl && annotationsMap[annId].streamUrl === firstAnnotationAudioUrl;
          if (isShownAsWaveform) {
            node.style.display = 'none';
            // Remove trailing <br> / empty text nodes so they don't render as phantom gap
            let next = node.nextSibling;
            while (next && (next.nodeName === 'BR' || (next.nodeType === 3 && next.textContent.trim() === ''))) {
              const toRemove = next;
              next = next.nextSibling;
              toRemove.parentNode.removeChild(toRemove);
            }
          } else {
            node.classList.add('practice-annotation');
            node.classList.add('annotation-hidden');
            if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '0');
            if (!node.hasAttribute('role')) node.setAttribute('role', 'button');
            node.setAttribute('aria-label', 'Play audio');
          }
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      console.warn('EssayWriting: failed to enhance reading text HTML', error);
      return readingText;
    }
  }, [readingText, annotationsMap, questionLevelAudioUrl, firstAnnotationAudioUrl]);

  // Enhance HTML with annotation classes for prompt; hide annotation shown via waveform player
  const enhancedPrompt = useMemo(() => {
    if (!prompt || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      return prompt;
    }

    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(prompt, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');

      nodes.forEach((node) => {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]) {
          const isShownAsWaveform = !questionLevelAudioUrl && annotationsMap[annId].streamUrl === firstAnnotationAudioUrl;
          if (isShownAsWaveform) {
            node.style.display = 'none';
            // Remove trailing <br> / empty text nodes so they don't render as phantom gap
            let next = node.nextSibling;
            while (next && (next.nodeName === 'BR' || (next.nodeType === 3 && next.textContent.trim() === ''))) {
              const toRemove = next;
              next = next.nextSibling;
              toRemove.parentNode.removeChild(toRemove);
            }
          } else {
            node.classList.add('practice-annotation');
            node.classList.add('annotation-hidden');
            if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '0');
            if (!node.hasAttribute('role')) node.setAttribute('role', 'button');
            node.setAttribute('aria-label', 'Play audio');
          }
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      console.warn('EssayWriting: failed to enhance prompt HTML', error);
      return prompt;
    }
  }, [prompt, annotationsMap, questionLevelAudioUrl, firstAnnotationAudioUrl]);

  const highlightAnnotation = useCallback((element) => {
    if (!element) return;

    if (activeAnnotationRef.current && activeAnnotationRef.current !== element) {
      activeAnnotationRef.current.classList.remove('practice-annotation-active');
    }

    activeAnnotationRef.current = element;
    element.classList.add('practice-annotation-active');
  }, []);

  const handlePlayAnnotation = useCallback((annotation) => {
    const streamUrl = annotation?.streamUrl;
    if (!streamUrl) return;
    setActiveAudioUrl(streamUrl);
    setAutoPlayRequested(true);
  }, []);

  const handleAudioEnded = useCallback(() => {
    if (activeAnnotationRef.current) {
      activeAnnotationRef.current.classList.remove('practice-annotation-active');
      activeAnnotationRef.current = null;
    }
  }, []);

  const findAnnotationTrigger = (event) => {
    const target = event.target;
    if (target && typeof target.closest === 'function') {
      return target.closest('[data-ann-id]');
    }
    return null;
  };

  const handleAnnotationTrigger = (event) => {
    // Don't handle if clicking on textarea
    if (event.target.closest('textarea')) {
      return;
    }

    console.log('🎯 EssayWriting - Annotation clicked:', {
      target: event.target,
      annotationsMapSize: annotationsMap ? Object.keys(annotationsMap).length : 0
    });

    if (!annotationsMap || Object.keys(annotationsMap).length === 0) {
      console.warn('EssayWriting: No annotations map available');
      return;
    }

    const trigger = findAnnotationTrigger(event);
    if (!trigger) {
      console.warn('EssayWriting: No annotation trigger found');
      return;
    }

    const annId = trigger.getAttribute('data-ann-id');
    console.log('🎯 EssayWriting - Found annotation ID:', annId);
    
    if (!annId || !annotationsMap[annId]) {
      console.warn('EssayWriting: Annotation not found in map:', {
        annId,
        availableIds: Object.keys(annotationsMap)
      });
      return;
    }

    const annotation = annotationsMap[annId];
    console.log('🎯 EssayWriting - Using annotation:', annotation);
    highlightAnnotation(trigger);
    handlePlayAnnotation(annotation);
  };

  const handleAnnotationKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleAnnotationTrigger(event);
  };

  const hasAnnotations = annotationsMap && Object.keys(annotationsMap).length > 0;

  // Attach event listeners to annotation elements after HTML is rendered
  useEffect(() => {
    if (!hasAnnotations || !annotationsMap) return;

    const containers = [];
    if (readingTextRef.current) containers.push(readingTextRef.current);
    if (promptRef.current) containers.push(promptRef.current);

    const handleAudioClick = (event) => {
      const span = event.currentTarget;
      const annId = span?.getAttribute('data-ann-id');
      if (!annId || !annotationsMap[annId]) return;

      const annotation = annotationsMap[annId];
      if (annotation) {
        highlightAnnotation(span);
        handlePlayAnnotation(annotation);
      }
    };

    const handleAudioKeyDown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      handleAudioClick(event);
    };

    containers.forEach((container) => {
      const audioSpans = Array.from(container.querySelectorAll('[data-ann-id][data-ann-type="audio"]'));
      audioSpans.forEach((span) => {
        const annId = span.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]) {
          // Remove existing listeners to avoid duplicates
          span.removeEventListener('click', handleAudioClick);
          span.removeEventListener('keydown', handleAudioKeyDown);
          
          // Add new listeners
          span.addEventListener('click', handleAudioClick);
          span.addEventListener('keydown', handleAudioKeyDown);
          
          // Ensure proper styling and attributes
          span.classList.add('practice-annotation', 'annotation-hidden');
          if (!span.hasAttribute('tabindex')) {
            span.setAttribute('tabindex', '0');
          }
          if (!span.hasAttribute('role')) {
            span.setAttribute('role', 'button');
          }
        }
      });
    });

    // Cleanup function
    return () => {
      containers.forEach((container) => {
        const audioSpans = Array.from(container.querySelectorAll('[data-ann-id][data-ann-type="audio"]'));
        audioSpans.forEach((span) => {
          span.removeEventListener('click', handleAudioClick);
          span.removeEventListener('keydown', handleAudioKeyDown);
        });
      });
    };
  }, [hasAnnotations, annotationsMap, readingText, prompt, highlightAnnotation, handlePlayAnnotation]);

  const handleSubmit = () => {
    if (onAnswer) {
      onAnswer({
        questionId: question['soal-id'] || question.id,
        answer: essay.trim(),
        isCorrect: false // Will be determined by backend
      });
    }
    if (onNext) onNext();
  };

  return (
    <motion.div
      className="flex flex-col gap-5 max-md:gap-4 max-md:pb-10"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex justify-between items-center pb-4 border-b-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)] max-md:flex-col max-md:items-start max-md:gap-3">
        <motion.span
          className="px-3.5 py-2 bg-gradient-to-br from-[#12766d] to-[#236463] text-white rounded-lg text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shrink-0 max-md:text-[10px] max-md:px-2 max-md:py-1"
          initial={{ scale: 0.85 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          Essay Writing
        </motion.span>
      </div>

      <div className="flex flex-col gap-5">
        {displayAudioUrl && (
          <QuestionAudioPlayer
            audioUrl={displayAudioUrl}
            autoPlay={autoPlayRequested}
            onPlayStarted={() => setAutoPlayRequested(false)}
            onEnded={handleAudioEnded}
          />
        )}
        {readingText && (
          <div className="mb-6 p-5 bg-[rgba(18,118,109,0.05)] dark:bg-[rgba(20,184,166,0.08)] rounded-xl border-l-4 border-[#12766d] dark:border-[#14b8a6] max-md:p-4 max-md:mb-5">
            <div
              ref={readingTextRef}
              className="passage-text text-base leading-relaxed text-[var(--text-primary)] whitespace-pre-line"
              onClick={hasAnnotations ? handleAnnotationTrigger : undefined}
              onKeyDown={hasAnnotations ? handleAnnotationKeyDown : undefined}
              dangerouslySetInnerHTML={{ __html: enhancedReadingText }}
            />
          </div>
        )}

        {prompt && (
          <p
            ref={promptRef}
            className="question-text text-lg font-semibold text-[var(--text-primary)] leading-relaxed whitespace-pre-line"
            onClick={hasAnnotations ? handleAnnotationTrigger : undefined}
            onKeyDown={hasAnnotations ? handleAnnotationKeyDown : undefined}
            dangerouslySetInnerHTML={{ __html: enhancedPrompt }}
          />
        )}

        {questionText && (
          <div className="mt-4 mb-4 whitespace-pre-line text-[var(--text-primary)]">
            {questionText}
          </div>
        )}

        <div className="mt-2 relative max-md:mt-1 [&_.umlaut-helper]:absolute [&_.umlaut-helper]:top-3 [&_.umlaut-helper]:right-3 [&_.umlaut-helper]:z-10 max-md:[&_.umlaut-helper]:hidden">
          <UmlautButtons
            inputRef={textareaRef}
            langCode={langCode}
            onInsertChar={(char) => {
              const textarea = textareaRef.current;
              if (textarea) {
                const start = textarea.selectionStart || 0;
                const end = textarea.selectionEnd || 0;
                const newValue = essay.substring(0, start) + char + essay.substring(end);
                setEssay(newValue);
                setTimeout(() => {
                  textarea.focus();
                  textarea.setSelectionRange(start + char.length, start + char.length);
                }, 0);
              }
            }}
          />
          <textarea
            ref={textareaRef}
            className="w-full p-4 pr-[60px] max-md:pr-[50px] max-md:p-3.5 border-2 border-[rgba(18,118,109,0.2)] dark:border-[rgba(20,184,166,0.25)] rounded-xl text-base text-[var(--text-primary)] bg-[var(--card-bg)] dark:bg-[var(--bg-secondary)] resize-y transition-all duration-300 leading-relaxed min-h-[200px] max-md:min-h-[150px] focus:outline-none focus:border-[#12766d] focus:shadow-[0_0_0_3px_rgba(18,118,109,0.1)] dark:focus:border-[#14b8a6] dark:focus:shadow-[0_0_0_3px_rgba(20,184,166,0.15)] placeholder:opacity-50 placeholder:text-[rgba(26,26,46,0.4)] dark:placeholder:text-[var(--text-tertiary)] font-[inherit]"
            value={essay}
            onChange={(e) => setEssay(e.target.value)}
            placeholder="Write your answer here..."
            rows={10}
          />
          <UmlautHelper langCode={langCode} />
          <div className="mt-3 max-md:mt-2.5 flex items-center gap-2 text-sm max-md:text-[13px] font-semibold text-[rgba(26,26,46,0.6)] dark:text-[var(--text-secondary)]">
            <span>{wordCount}</span> kata
          </div>
        </div>
      </div>

      <div className="flex justify-center pt-4 border-t-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)]">
        <motion.button
          type="button"
          className="py-3.5 px-7 rounded-xl border-none text-base font-semibold cursor-pointer transition-all duration-300 bg-gradient-to-br from-[#12766d] to-[#0f5d56] text-white shadow-[0_4px_12px_rgba(18,118,109,0.3)] hover:shadow-[0_6px_16px_rgba(18,118,109,0.4)] hover:-translate-y-0.5 dark:bg-gradient-to-br dark:from-[#fbbf24] dark:to-[#f59e0b] dark:text-[#1d4746] dark:shadow-[0_4px_14px_rgba(251,191,36,0.35)] dark:hover:from-[#f59e0b] dark:hover:to-[#d97706] dark:hover:shadow-[0_6px_18px_rgba(251,191,36,0.45)] max-md:w-full max-md:py-3.5 max-md:px-6 max-md:min-h-[44px] max-md:text-[15px] touch-manipulation"
          onClick={handleSubmit}
          whileTap={{ scale: 0.96 }}
        >
          Submit
        </motion.button>
      </div>
    </motion.div>
  );
};

export default EssayWriting;

